import * as Yup from 'yup';
import { TFunction } from 'react-i18next';
import {
  IScheduleConstraints,
  IScheduleDayConstraints,
  ITournamentSchedule,
  IFreeSlotItem,
  isFreeSlot,
  IScheduleItem,
  IDaySchedule,
  IMatchScheduleItem,
  IGlobalScheduleSettings,
} from '../../../../types/schedule';
import { ClassFormatEnum, ClassMatchType, ScheduleFilterType } from '../../../../types/enums';
import { Class, Court, TournamentState } from '../../../../types/tournament';
import { getTimezoneForCountry } from '../../../../utils/timezone';
import { addDays } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { deepCloneSerializable } from '../../../../utils/deepCloneSerializable';
import { dedupeScheduledMatchesById } from '../../../../features/schedule/scheduleOverlap';

export type ScheduleFormikValues = IGlobalScheduleSettings & Record<string, any>;

export const ROUND_KEYS = [
  { key: 'group', label: 'Group Phase' },
  { key: 'round_1', label: 'Round 1' },
  { key: 'round_2', label: 'Round 2' },
  { key: 'round_3', label: 'Round 3' },
  { key: 'round_4', label: 'Round 4' },
  { key: 'round_5', label: 'Round 5' },
  { key: 'round_6', label: 'Round 6' },
  { key: 'round_7', label: 'Round 7' },
  { key: 'round_8', label: 'Round 8' },
  { key: 'round_9', label: 'Round 9' },
  { key: 'round_10', label: 'Round 10' },
  { key: 'round_11', label: 'Quarters' },
  { key: 'round_12', label: 'Semis' },
  { key: 'round_13', label: 'Gold/Bronze' }
];

export function getTournamentTimeZone(tournamentInfo: TournamentState | null | undefined): string {
  if (!tournamentInfo) return 'UTC';
  return tournamentInfo.timeZone || getTimezoneForCountry(tournamentInfo.country) || 'UTC';
}

export function isMatchScheduledOnGrid(match: IMatchScheduleItem): boolean {
  return Boolean((match.scheduleDate || match.startDate) && match.court);
}

/** Scheduled in DB even when the grid renderer returned the row as "unscheduled". */
export function isMatchScheduledInDatabase(match: IMatchScheduleItem): boolean {
  if (!match.scheduleDate && !match.startDate) return false;
  const courtId = match.court?.id ?? (match as { courtId?: number | null }).courtId;
  return courtId != null;
}

export function clearMatchExplicitEndTimes(
  match: IMatchScheduleItem
): Pick<IMatchScheduleItem, 'scheduleEndDate' | 'endDate'> {
  return {
    scheduleEndDate: null,
    endDate: null,
  };
}

export function stripMatchToUnscheduled(match: IMatchScheduleItem): IMatchScheduleItem {
  return {
    ...match,
    scheduleDate: null,
    startDate: null,
    ...clearMatchExplicitEndTimes(match),
    court: null,
  } as unknown as IMatchScheduleItem;
}

const MAX_LEGACY_ENDDATE_SLOT_MINUTES = 8 * 60;

function getExplicitMatchEndMs(match: IMatchScheduleItem): number | null {
  if (match.scheduleEndDate) {
    return new Date(match.scheduleEndDate).getTime();
  }
  if (match.endDate) {
    return new Date(match.endDate).getTime();
  }
  return null;
}

export function hasInvalidScheduleInterval(
  match: IMatchScheduleItem,
  _constraints?: IScheduleConstraints | null
): boolean {
  if (!isMatchScheduledOnGrid(match)) return false;
  const startMs = getMatchStartMs(match);
  if (startMs == null) return false;
  const explicitEndMs = getExplicitMatchEndMs(match);
  if (explicitEndMs == null || !Number.isFinite(explicitEndMs)) return false;
  return explicitEndMs <= startMs;
}

export type SanitizeScheduledMatchesResult = {
  scheduled: IMatchScheduleItem[];
  unscheduled: IMatchScheduleItem[];
};

export function sanitizeScheduledMatches(
  matches: IMatchScheduleItem[],
  constraints: IScheduleConstraints
): SanitizeScheduledMatchesResult {
  const scheduled: IMatchScheduleItem[] = [];
  const unscheduled: IMatchScheduleItem[] = [];

  for (const match of matches) {
    if (!isMatchScheduledOnGrid(match)) {
      if (match.scheduleDate || match.startDate) {
        scheduled.push(match);
      }
      continue;
    }
    if (hasInvalidScheduleInterval(match, constraints)) {
      unscheduled.push(stripMatchToUnscheduled(match));
      continue;
    }
    scheduled.push(match);
  }

  return { scheduled, unscheduled };
}

export function generateInitialScheduleValues(
  constraints: IScheduleConstraints,
  timeZone: string
): ScheduleFormikValues {
  const initialValues: ScheduleFormikValues = {
    allow_split: constraints.allow_split ?? true,
    allow_alignment: constraints.allow_alignment ?? false,
    pull_to_lower_court: constraints.pull_to_lower_court ?? false,
    gap_group_group: constraints.gap_group_group ?? 0,
    max_gap_group_group: constraints.max_gap_group_group ?? 20,
    gap_group_ko: constraints.gap_group_ko ?? 20,
    max_gap_group_ko: constraints.max_gap_group_ko ?? 40,
    gap_ko_ko: constraints.gap_ko_ko ?? 0,
    max_gap_ko_ko: constraints.max_gap_ko_ko ?? 20,
    max_span_limit: constraints.max_span_limit,
    s1_time_limit: constraints.s1_time_limit ?? 10,
    s2_time_limit: constraints.s2_time_limit ?? 5,
    stream_courts: [],
    max_chunk_size: 2,
    number_of_chunk_splits: 1,
    classOverrides: {},
  };

  const overrides = constraints.classOverrides || {};
  const formattedOverrides: Record<string, any> = {};

  Object.keys(overrides).forEach(classIdStr => {
    const raw = overrides[Number(classIdStr)];
    const safeMatchLengths = raw.matchLengths || {};

    const customDurations = Object.keys(safeMatchLengths).map(key => ({
      roundKey: key,
      duration: safeMatchLengths[key]
    }));

    formattedOverrides[classIdStr] = {
      ...raw,
      customDurations
    };
  });
  initialValues.classOverrides = formattedOverrides;

  const globalLengths = (constraints as any).globalMatchLengths || {};
  ROUND_KEYS.forEach(({ key }) => {
    if (key !== 'knockout_default') {
      initialValues[`globalMatchLength_${key}`] = globalLengths[key] || 25;
    }
  });

  constraints.days.forEach((day) => {
    const allClassesChecked = constraints.tournamentClasses.every(({ id }) => {
      return day.classes.map(({ id: classId }) => classId).includes(id);
    });
    const allCourtsChecked = constraints.tournamentCourts.every(({ id }) => {
      return day.courts.map(({ id: courtId }) => courtId).includes(id);
    });

    initialValues[`startOfDay-${day.dayKey}`] = getHhmmTimeString(day.startTime, timeZone);
    initialValues[`endOfDay-${day.dayKey}`] = getHhmmTimeString(day.endTime, timeZone);

    initialValues[`all-class-${day.dayKey}`] = allClassesChecked;
    initialValues[`all-court-${day.dayKey}`] = allCourtsChecked;

    initialValues[`class-day-${day.dayKey}`] = day.classes
      .filter(c => !c.parentClassId)
      .map(({ id }) => id.toString());

    initialValues[`court-day-${day.dayKey}`] = day.courts.map(({ id }) => id.toString());
  });
  return initialValues;
}

export type EffectiveDayBounds = {
  effectiveStartMs: number;
  effectiveEndMs: number;
  outOfWindow: boolean;
};

export function getMatchStartMs(match: IMatchScheduleItem): number | null {
  const dateStr = match.scheduleDate || match.startDate;
  if (!dateStr) return null;
  return new Date(dateStr).getTime();
}

export function getMatchEndMs(
  match: IMatchScheduleItem,
  constraints: IScheduleConstraints
): number | null {
  const startMs = getMatchStartMs(match);
  if (startMs == null) return null;
  if (match.scheduleEndDate) {
    const explicitEnd = new Date(match.scheduleEndDate).getTime();
    if (Number.isFinite(explicitEnd) && explicitEnd > startMs) {
      return explicitEnd;
    }
  }
  if (match.endDate) {
    const legacyEnd = new Date(match.endDate).getTime();
    if (legacyEnd > startMs) {
      const legacyDuration = Math.round((legacyEnd - startMs) / 60000);
      if (legacyDuration > 0 && legacyDuration <= MAX_LEGACY_ENDDATE_SLOT_MINUTES) {
        return legacyEnd;
      }
    }
  }
  const duration = match.duration || getMatchDurationFromConstraints(match, constraints) || 30;
  return startMs + duration * 60000;
}

export function getEffectiveDayBounds(
  dayConstraints: IScheduleDayConstraints,
  matchesOnDay: IMatchScheduleItem[],
  constraints: IScheduleConstraints,
  timeZone?: string
): EffectiveDayBounds {
  const constraintStartMs = new Date(dayConstraints.startTime).getTime();
  const constraintEndMs = timeZone
    ? resolveDayConstraintEndMs(dayConstraints, timeZone)
    : new Date(dayConstraints.endTime).getTime();

  let matchEarliest: number | null = null;
  let matchLatest: number | null = null;

  for (const match of matchesOnDay) {
    const startMs = getMatchStartMs(match);
    if (startMs == null) continue;
    const endMs = getMatchEndMs(match, constraints) ?? startMs;
    if (matchEarliest == null || startMs < matchEarliest) matchEarliest = startMs;
    if (matchLatest == null || endMs > matchLatest) matchLatest = endMs;
  }

  const effectiveStartMs =
    matchEarliest != null ? Math.min(constraintStartMs, matchEarliest) : constraintStartMs;
  const effectiveEndMs =
    matchLatest != null ? Math.max(constraintEndMs, matchLatest) : constraintEndMs;

  const outOfWindow =
    matchEarliest != null &&
    (matchEarliest < constraintStartMs || (matchLatest != null && matchLatest > constraintEndMs));

  return { effectiveStartMs, effectiveEndMs, outOfWindow };
}

export function getNextTournamentDay(
  dayConstraints: IScheduleDayConstraints,
  constraints: IScheduleConstraints
): IScheduleDayConstraints | null {
  return (
    constraints.days
      .filter((d) => d.dayKey > dayConstraints.dayKey)
      .sort((a, b) => a.dayKey - b.dayKey)[0] ?? null
  );
}

function getPreviousTournamentDay(
  dayConstraints: IScheduleDayConstraints,
  constraints: IScheduleConstraints
): IScheduleDayConstraints | null {
  return (
    constraints.days
      .filter((d) => d.dayKey < dayConstraints.dayKey)
      .sort((a, b) => b.dayKey - a.dayKey)[0] ?? null
  );
}

function nextCalendarDayStr(dateStr: string, timeZone: string): string {
  const noon = fromZonedTime(`${dateStr}T12:00:00`, timeZone);
  return formatInTimeZone(addDays(noon, 1), timeZone, 'yyyy-MM-dd');
}

/** Session end on the tournament day's start calendar date + end clock (handles drifted endTime dates). */
export function resolveDayConstraintEndMs(
  dayConstraints: IScheduleDayConstraints,
  timeZone: string
): number {
  const startMs = new Date(dayConstraints.startTime).getTime();
  const startDateStr = formatInTimeZone(new Date(dayConstraints.startTime), timeZone, 'yyyy-MM-dd');
  const endClock = formatInTimeZone(new Date(dayConstraints.endTime), timeZone, 'HH:mm:ss');
  let endMs = fromZonedTime(`${startDateStr}T${endClock}`, timeZone).getTime();
  if (endMs <= startMs) {
    endMs = fromZonedTime(
      `${nextCalendarDayStr(startDateStr, timeZone)}T${endClock}`,
      timeZone
    ).getTime();
  }
  return endMs;
}

export function getTournamentDayOvernightCapMs(
  dayConstraints: IScheduleDayConstraints,
  constraints: IScheduleConstraints,
  timeZone: string
): number {
  const nextDay = getNextTournamentDay(dayConstraints, constraints);
  if (nextDay) {
    return new Date(nextDay.startTime).getTime();
  }

  const startDateStr = formatInTimeZone(new Date(dayConstraints.startTime), timeZone, 'yyyy-MM-dd');
  const endClock = formatInTimeZone(new Date(dayConstraints.endTime), timeZone, 'HH:mm:ss');
  return fromZonedTime(`${nextCalendarDayStr(startDateStr, timeZone)}T${endClock}`, timeZone).getTime();
}

export function getTournamentDaySessionEndMs(
  dayConstraints: IScheduleDayConstraints,
  constraints: IScheduleConstraints,
  timeZone: string,
  matchesOnDay: IMatchScheduleItem[] = []
): number {
  const constraintEndMs = resolveDayConstraintEndMs(dayConstraints, timeZone);
  const { effectiveEndMs } = getEffectiveDayBounds(
    dayConstraints,
    matchesOnDay,
    constraints,
    timeZone
  );
  const overnightCapMs = getTournamentDayOvernightCapMs(dayConstraints, constraints, timeZone);
  const baseEnd = Math.max(constraintEndMs, effectiveEndMs);
  const sessionEndMs = Math.min(baseEnd, overnightCapMs);
  return sessionEndMs;
}

export type TournamentDayDisplayBounds = EffectiveDayBounds & {
  sessionEndMs: number;
};

export function getTournamentDayDisplayBounds(
  dayConstraints: IScheduleDayConstraints,
  matchesOnDay: IMatchScheduleItem[],
  constraints: IScheduleConstraints,
  timeZone: string
): TournamentDayDisplayBounds {
  const bounds = getEffectiveDayBounds(dayConstraints, matchesOnDay, constraints, timeZone);
  const sessionEndMs = getTournamentDaySessionEndMs(
    dayConstraints,
    constraints,
    timeZone,
    matchesOnDay
  );
  return {
    ...bounds,
    effectiveEndMs: sessionEndMs,
    sessionEndMs,
  };
}

function findConstraintDayForCalendarDate(
  matchDayStr: string,
  constraints: IScheduleConstraints,
  timeZone: string
): IScheduleDayConstraints | undefined {
  return constraints.days.find(
    (day) =>
      formatInTimeZone(new Date(day.startTime), timeZone, 'yyyy-MM-dd') === matchDayStr
  );
}

/** Whether a match passes court/calendar bucketing for a tournament day (before effectiveStart filter). */
export function matchIsCourtCandidateForTournamentDay(
  match: IMatchScheduleItem,
  dayConstraints: IScheduleDayConstraints,
  constraints: IScheduleConstraints,
  timeZone: string
): boolean {
  const courtIds = new Set((dayConstraints.courts ?? []).map((c) => c.id));
  if (!match.court || !courtIds.has(match.court.id)) return false;

  const startMs = getMatchStartMs(match);
  if (startMs == null) return false;

  const overnightCapMs = getTournamentDayOvernightCapMs(dayConstraints, constraints, timeZone);
  if (startMs >= overnightCapMs) return false;

  const effectiveDate = match.scheduleDate || match.startDate;
  if (!effectiveDate) return false;

  const dayStartStr = formatInTimeZone(new Date(dayConstraints.startTime), timeZone, 'yyyy-MM-dd');
  const constraintStartMs = new Date(dayConstraints.startTime).getTime();
  const matchDayStr = formatInTimeZone(new Date(effectiveDate), timeZone, 'yyyy-MM-dd');

  if (startMs >= constraintStartMs) {
    return matchDayStr === dayStartStr;
  }

  const prevDay = getPreviousTournamentDay(dayConstraints, constraints);
  if (prevDay && matchDayStr !== dayStartStr) {
    const prevOvernightCapMs = getTournamentDayOvernightCapMs(prevDay, constraints, timeZone);
    if (startMs < prevOvernightCapMs) return false;
  }

  if (matchDayStr === dayStartStr && startMs < constraintStartMs) return true;
  if (matchDayStr > dayStartStr && startMs < overnightCapMs) {
    const ownerDay = findConstraintDayForCalendarDate(matchDayStr, constraints, timeZone);
    if (ownerDay && ownerDay.dayKey > dayConstraints.dayKey) return false;
    return true;
  }

  return false;
}

/** Single-match check equivalent to membership in getMatchesForTournamentDay result. */
export function matchBelongsToTournamentDay(
  match: IMatchScheduleItem,
  dayConstraints: IScheduleDayConstraints,
  constraints: IScheduleConstraints,
  timeZone: string,
  matchesForBounds: IMatchScheduleItem[]
): boolean {
  if (!matchIsCourtCandidateForTournamentDay(match, dayConstraints, constraints, timeZone)) {
    return false;
  }

  const courtCandidates = matchesForBounds.filter((m) =>
    matchIsCourtCandidateForTournamentDay(m, dayConstraints, constraints, timeZone)
  );
  const { effectiveStartMs } = getEffectiveDayBounds(
    dayConstraints,
    courtCandidates,
    constraints,
    timeZone
  );
  const startMs = getMatchStartMs(match);
  return startMs != null && startMs >= effectiveStartMs;
}

export type TournamentDayMatchIndex = {
  matchesByDayKey: Map<number, IMatchScheduleItem[]>;
  dayKeyByMatchId: Map<number, number>;
  dayByKey: Map<number, IScheduleDayConstraints>;
};

export function buildTournamentDayMatchIndex(
  allMatches: IMatchScheduleItem[],
  constraints: IScheduleConstraints,
  timeZone: string
): TournamentDayMatchIndex {
  const matchesByDayKey = new Map<number, IMatchScheduleItem[]>();
  const dayKeyByMatchId = new Map<number, number>();
  const dayByKey = new Map<number, IScheduleDayConstraints>();

  for (const day of constraints.days) {
    dayByKey.set(day.dayKey, day);
    const dayMatches = getMatchesForTournamentDay(day, allMatches, constraints, timeZone);
    matchesByDayKey.set(day.dayKey, dayMatches);
    for (const m of dayMatches) {
      dayKeyByMatchId.set(m.id, day.dayKey);
    }
  }

  return { matchesByDayKey, dayKeyByMatchId, dayByKey };
}

export function resolveTournamentDayKeyForMatch(
  match: IMatchScheduleItem,
  constraints: IScheduleConstraints,
  timeZone: string
): number | null {
  if (!match.scheduleDate || !match.court) return null;

  const matchingDays = constraints.days.filter((day) =>
    matchBelongsToTournamentDay(match, day, constraints, timeZone, [match])
  );

  if (matchingDays.length === 0) {
    const matchDayStr = formatInTimeZone(new Date(match.scheduleDate), timeZone, 'yyyy-MM-dd');
    const fallback = constraints.days.find(
      (d) => formatInTimeZone(new Date(d.startTime), timeZone, 'yyyy-MM-dd') === matchDayStr
    );
    return fallback?.dayKey ?? null;
  }

  if (matchingDays.length === 1) {
    return matchingDays[0].dayKey;
  }

  const matchDayStr = formatInTimeZone(new Date(match.scheduleDate), timeZone, 'yyyy-MM-dd');
  return (
    matchingDays.find(
      (d) => formatInTimeZone(new Date(d.startTime), timeZone, 'yyyy-MM-dd') === matchDayStr
    ) ?? matchingDays[0]
  ).dayKey;
}

export function getMatchesForTournamentDay(
  dayConstraints: IScheduleDayConstraints,
  allMatches: IMatchScheduleItem[],
  constraints: IScheduleConstraints,
  timeZone: string
): IMatchScheduleItem[] {
  const courtCandidates = allMatches.filter((m) =>
    matchIsCourtCandidateForTournamentDay(m, dayConstraints, constraints, timeZone)
  );

  const { effectiveStartMs } = getEffectiveDayBounds(
    dayConstraints,
    courtCandidates,
    constraints,
    timeZone
  );

  return courtCandidates.filter((m) => {
    const startMs = getMatchStartMs(m);
    if (startMs == null) return false;
    return startMs >= effectiveStartMs;
  });
}

export function resolveTournamentDayForMatch(
  match: IMatchScheduleItem,
  constraints: IScheduleConstraints,
  timeZone: string,
  allMatches?: IMatchScheduleItem[]
): IScheduleDayConstraints | null {
  if (!match.scheduleDate || !match.court) return null;

  const pool = allMatches ?? [match];
  const matchingDays = constraints.days.filter((day) =>
    getMatchesForTournamentDay(day, pool, constraints, timeZone).some((m) => m.id === match.id)
  );

  if (matchingDays.length === 0) {
    const matchDayStr = formatInTimeZone(new Date(match.scheduleDate), timeZone, 'yyyy-MM-dd');
    return (
      constraints.days.find(
        (d) => formatInTimeZone(new Date(d.startTime), timeZone, 'yyyy-MM-dd') === matchDayStr
      ) ?? null
    );
  }

  if (matchingDays.length === 1) {
    return matchingDays[0];
  }

  const matchDayStr = formatInTimeZone(new Date(match.scheduleDate), timeZone, 'yyyy-MM-dd');
  return (
    matchingDays.find(
      (d) => formatInTimeZone(new Date(d.startTime), timeZone, 'yyyy-MM-dd') === matchDayStr
    ) ?? matchingDays[0]
  );
}

export function scheduleHasOutOfWindowMatches(
  schedule: ITournamentSchedule | null,
  constraints: IScheduleConstraints | null,
  timeZone: string
): boolean {
  if (!schedule || !constraints) return false;

  for (const day of schedule.days) {
    const dayConstraints = constraints.days.find((d) => d.dayKey === day.dayKey);
    if (!dayConstraints) continue;

    const matchesOnDay = day.courts.flatMap(
      (courtSchedule) =>
        courtSchedule.matches.filter((m) => !isFreeSlot(m)) as IMatchScheduleItem[]
    );

    if (getEffectiveDayBounds(dayConstraints, matchesOnDay, constraints, timeZone).outOfWindow) {
      return true;
    }
  }

  return false;
}

const FREE_SLOT_MINUTES = 5;

function buildCourtTimelineForDay(
  courtMatches: IMatchScheduleItem[],
  dayKey: number,
  court: Court,
  constraints: IScheduleConstraints,
  effectiveStartMs: number,
  effectiveEndMs: number
): IScheduleItem[] {
  const timeline: IScheduleItem[] = [];
  let currentTime = effectiveStartMs;

  for (const match of courtMatches) {
    const updatedMatch = { ...match };
    const matchStart = getMatchStartMs(updatedMatch);
    if (matchStart == null) continue;

    while (currentTime < matchStart) {
      const diffMins = (matchStart - currentTime) / 60000;
      const dur = Math.min(FREE_SLOT_MINUTES, diffMins);
      timeline.push({
        isFreeSlot: true,
        day: dayKey,
        court,
        scheduleDate: new Date(currentTime).toISOString(),
        duration: dur,
      });
      currentTime += dur * 60000;
    }

    const matchDuration =
      updatedMatch.duration || getMatchDurationFromConstraints(updatedMatch, constraints) || 30;
    updatedMatch.duration = matchDuration;
    const matchEnd = matchStart + matchDuration * 60000;

    updatedMatch.scheduleEndDate = new Date(matchEnd).toISOString();
    timeline.push(updatedMatch);

    currentTime = matchEnd;
  }

  while (currentTime < effectiveEndMs) {
    const diffMins = (effectiveEndMs - currentTime) / 60000;
    const dur = Math.min(FREE_SLOT_MINUTES, diffMins);
    timeline.push({
      isFreeSlot: true,
      day: dayKey,
      court,
      scheduleDate: new Date(currentTime).toISOString(),
      duration: dur,
    });
    currentTime += dur * 60000;
  }

  return timeline;
}

export const rebuildEntireSchedule = (
  allMatches: IMatchScheduleItem[],
  constraints: IScheduleConstraints,
  timeZone: string
): ITournamentSchedule => {
  const schedule: ITournamentSchedule = { days: [] };
  const uniqueMatches = dedupeScheduledMatchesById(allMatches);
  const placementMatches = uniqueMatches.filter((m) => m.scheduleDate || m.startDate);
  const { scheduled: sanitizedMatches } = sanitizeScheduledMatches(placementMatches, constraints);

  const cloneDateWithDay = (source: string, dayStr: string) => {
    const timeStr = formatInTimeZone(new Date(source), timeZone, 'HH:mm:ss');
    return fromZonedTime(`${dayStr}T${timeStr}`, timeZone).toISOString();
  };

  const isMatchWithinDayWindow = (matchDateMs: number, dayStartMs: number, dayEndMs: number) =>
    matchDateMs >= dayStartMs && matchDateMs < dayEndMs;

  const scheduleDays: IScheduleDayConstraints[] = [...constraints.days];
  const configuredDayStrings = new Set(
    scheduleDays.map((day) => formatInTimeZone(new Date(day.startTime), timeZone, 'yyyy-MM-dd'))
  );
  const syntheticDayStrings = new Set<string>();
  const maxDayKey = scheduleDays.reduce((max, day) => Math.max(max, day.dayKey), -1);

  sanitizedMatches.forEach((match) => {
    const effectiveDate = match.scheduleDate || match.startDate;
    if (!effectiveDate || !match.court) return;

    const matchDayStr = formatInTimeZone(new Date(effectiveDate), timeZone, 'yyyy-MM-dd');
    if (configuredDayStrings.has(matchDayStr) || syntheticDayStrings.has(matchDayStr)) {
      return;
    }

    const matchClassId = match.classId;
    const templateDay = constraints.days.find((day) => {
      const courtIds = day.courts?.map((c) => c.id) || [];
      const classIds = day.classes?.map((tournamentClass) => tournamentClass.id) || [];
      return courtIds.includes(match.court.id) && classIds.includes(Number(matchClassId));
    });

    if (!templateDay) return;

    const syntheticStartTime = cloneDateWithDay(templateDay.startTime, matchDayStr);
    const syntheticEndTime = cloneDateWithDay(templateDay.endTime, matchDayStr);
    const matchDateMs = new Date(effectiveDate).getTime();

    if (
      !isMatchWithinDayWindow(
        matchDateMs,
        new Date(syntheticStartTime).getTime(),
        new Date(syntheticEndTime).getTime()
      )
    ) {
      return;
    }

    syntheticDayStrings.add(matchDayStr);
    scheduleDays.push({
      ...templateDay,
      dayKey: maxDayKey + syntheticDayStrings.size,
      startTime: syntheticStartTime,
      endTime: syntheticEndTime,
    });
  });

  for (const day of scheduleDays) {
    const dayMatches = getMatchesForTournamentDay(day, sanitizedMatches, constraints, timeZone);
    const { effectiveStartMs, sessionEndMs } = getTournamentDayDisplayBounds(
      day,
      dayMatches,
      constraints,
      timeZone
    );

    const daySchedule: IDaySchedule = {
      dayKey: day.dayKey,
      courts: day.courts.map((court) => {
        const courtMatches = dayMatches
          .filter((m) => m.court.id === court.id)
          .sort(
            (a, b) =>
              (getMatchStartMs(a) ?? 0) - (getMatchStartMs(b) ?? 0)
          );

        return {
          court,
          matches: buildCourtTimelineForDay(
            courtMatches,
            day.dayKey,
            court,
            constraints,
            effectiveStartMs,
            sessionEndMs
          ),
        };
      }),
    };
    schedule.days.push(daySchedule);
  }
  return schedule;
};

export function normalizeScheduleWithConstraints(
  schedule: ITournamentSchedule,
  constraints: IScheduleConstraints,
  timeZone: string
): ITournamentSchedule {
  const matches = schedule.days.flatMap((day) =>
    day.courts.flatMap((court) =>
      court.matches.filter((m): m is IMatchScheduleItem => !isFreeSlot(m))
    )
  );
  return rebuildEntireSchedule(matches, constraints, timeZone);
}

export function generateScheduleSchema(
  days: IScheduleDayConstraints[],
  t: TFunction<'translation', undefined>
) {
  const prefixValidation = 'validation-';
  const yupSchema: any = {};

  days.forEach((day) => {
    yupSchema[`startOfDay-${day.dayKey}`] = Yup.string().required(t(prefixValidation + 'required'));
    yupSchema[`endOfDay-${day.dayKey}`] = Yup.string()
      .required(t(prefixValidation + 'required'))
      .test({
        name: 'endTime',
        exclusive: false,
        message: t(prefixValidation + 'endTime'),
        test: function (endTime) {
          const startTime = this.parent[`startOfDay-${day.dayKey}`];
          if (startTime && endTime) {
            const start = parseTime(startTime);
            const end = parseTime(endTime);
            return end > start;
          }
          return true;
        },
      });
  });

  ROUND_KEYS.forEach(({ key }) => {
    if (key !== 'knockout_default') {
      yupSchema[`globalMatchLength_${key}`] = Yup.number()
        .typeError(t(prefixValidation + 'mustBeANumber'))
        .required(t(prefixValidation + 'required'))
        .positive(t(prefixValidation + 'positive'))
        .integer(t(prefixValidation + 'integer'));
    }
  });

  return yupSchema;
}

function parseTime(timeString: string) {
  const [hours, minutes] = timeString.split(':');
  return new Date(0, 0, 0, +hours, +minutes);
}

export function getTimeDateFromFormikInput(
  time: string,
  dateIso: string,
  timeZone: string,
  options?: { referenceStartIso?: string }
) {
  if (!time) return dateIso;
  const anchorIso = options?.referenceStartIso ?? dateIso;
  const dateStr = formatInTimeZone(new Date(anchorIso), timeZone, 'yyyy-MM-dd');
  const localDateTimeString = `${dateStr}T${time}:00`;
  let resultMs = fromZonedTime(localDateTimeString, timeZone).getTime();
  if (options?.referenceStartIso) {
    const startMs = new Date(options.referenceStartIso).getTime();
    if (resultMs <= startMs) {
      resultMs = fromZonedTime(
        `${nextCalendarDayStr(dateStr, timeZone)}T${time}:00`,
        timeZone
      ).getTime();
    }
  }
  return new Date(resultMs).toISOString();
}

export function getClassOptionsFromFormikInput(
  values: any,
  day: IScheduleDayConstraints,
  allCourts: Court[]
) {
  return day.classOptions.map((option) => {
    const out = { ...option };
    let courtsForClass = values[`class-on-court-day-${day.dayKey}`];
    if (courtsForClass !== undefined) {
      courtsForClass = courtsForClass
        .filter((v: string) => {
          const a = v.split('-');
          return +a[0] === option.class.id;
        })
        .map((c: string) => +c.split('-')[1]);
      if (courtsForClass.length > 0) {
        out.courts = allCourts.filter(({ id }) => courtsForClass.includes(id));
      }
    }

    const defaultMatchLengthsForClass =
      values[`defaultMatchLength-class-${option.class.id}-day-${day.dayKey}`];
    const qfMatchLengthsForClass =
      values[`QF-matchLength-class-${option.class.id}-day-${day.dayKey}`];
    const sfMatchLengthsForClass =
      values[`SF-matchLength-class-${option.class.id}-day-${day.dayKey}`];
    const finalMatchLengthsForClass =
      values[`F-matchLength-class-${option.class.id}-day-${day.dayKey}`];

    out.matchLengths = Object.keys(ClassMatchType)
      .filter((k) => !Number.isNaN(+k))
      .reduce((accumulator, value) => {
        const coerced: number = +value;
        if (coerced === ClassMatchType.FINAL) {
          accumulator[coerced as keyof typeof accumulator] = finalMatchLengthsForClass;
        } else if (coerced === ClassMatchType.SEMIFINAL) {
          accumulator[coerced as keyof typeof accumulator] = sfMatchLengthsForClass;
        } else if (coerced === ClassMatchType.QUARTERFINAL) {
          accumulator[coerced as keyof typeof accumulator] = qfMatchLengthsForClass;
        } else {
          accumulator[coerced as keyof typeof accumulator] = defaultMatchLengthsForClass;
        }

        return accumulator;
      }, {} as { [matchType in ClassMatchType]: number });

    return out;
  });
}

export function getDateString(d: string, timeZone: string) {
  return formatInTimeZone(new Date(d), timeZone, 'yyyy-MM-dd');
}

export function getHhmmTimeString(datestring: string, timeZone: string) {
  if (!datestring) return '';
  return formatInTimeZone(new Date(datestring), timeZone, 'HH:mm');
}

export type FilterItem = {
  type: ScheduleFilterType;
  value: number | string;
};

/** Class/team filters mask occupied slots on the grid; placement UI must use canonical occupancy. */
export function hasScheduleOccupancyFilters(filters: FilterItem[]): boolean {
  return filters.some(
    (f) => f.type === ScheduleFilterType.CLASS || f.type === ScheduleFilterType.TEAM
  );
}

function deepCloneScheduleForFilter(schedule: ITournamentSchedule): ITournamentSchedule {
  return deepCloneSerializable(schedule);
}

export function pureFilterScheduleBy(
  schedule: ITournamentSchedule,
  filters: FilterItem[]
): ITournamentSchedule {
  if (filters.length === 0) {
    return schedule;
  }

  const filteredSchedule: ITournamentSchedule = deepCloneScheduleForFilter(schedule);

  const filtersByType = filters.reduce((acc, filter) => {
    if (!acc[filter.type]) acc[filter.type] = [];
    acc[filter.type].push(Number(filter.value));
    return acc;
  }, {} as Record<string, number[]>);

  filteredSchedule.days = filteredSchedule.days.map((day) => {
    day.courts = day.courts.map((courtSchedule) => {
      courtSchedule.matches = courtSchedule.matches
        .map((match) => {
          if (isFreeSlot(match)) {
            return { ...match, scheduleDate: match.scheduleDate };
          } else {
            return {
              ...match,
              scheduleDate: match.scheduleDate ? match.scheduleDate : null,
            };
          }
        })
        .map((match) => {
          if (isFreeSlot(match)) return match;

          let includeMatch: boolean = true;

          // Class filters
          if (filtersByType[ScheduleFilterType.CLASS]) {
            if (!filtersByType[ScheduleFilterType.CLASS].includes(Number(match.classId))) {
              includeMatch = false;
            }
          }

          // Team filters
          if (includeMatch && filtersByType[ScheduleFilterType.TEAM]) {
            const homeId = match.homeTeam?.id ? Number(match.homeTeam.id) : -1;
            const awayId = match.awayTeam?.id ? Number(match.awayTeam.id) : -1;

            if (!filtersByType[ScheduleFilterType.TEAM].includes(homeId) &&
              !filtersByType[ScheduleFilterType.TEAM].includes(awayId)) {
              includeMatch = false;
            }
          }

          // Player filters
          if (includeMatch && filtersByType[ScheduleFilterType.PLAYER]) {
            const hp1 = match.homePlayer1?.id ? Number(match.homePlayer1.id) : -1;
            const hp2 = match.homePlayer2?.id ? Number(match.homePlayer2.id) : -1;
            const ap1 = match.awayPlayer1?.id ? Number(match.awayPlayer1.id) : -1;
            const ap2 = match.awayPlayer2?.id ? Number(match.awayPlayer2.id) : -1;

            if (!filtersByType[ScheduleFilterType.PLAYER].includes(hp1) &&
              !filtersByType[ScheduleFilterType.PLAYER].includes(hp2) &&
              !filtersByType[ScheduleFilterType.PLAYER].includes(ap1) &&
              !filtersByType[ScheduleFilterType.PLAYER].includes(ap2)) {
              includeMatch = false;
            }
          }

          // Time filters (not used at the moment)
          if (includeMatch && filtersByType[ScheduleFilterType.TIME]) {
            if (!filtersByType[ScheduleFilterType.TIME].includes(match.scheduleDate?.toString() as any)) {
              includeMatch = false;
            }
          }

          if (includeMatch) {
            return match;
          } else {
            const m = match as IMatchScheduleItem;
            return {
              isFreeSlot: true,
              day: day.dayKey,
              court: m.court,
              scheduleDate: m.scheduleDate,
              duration: m.duration || 25,
              scheduleEndDate: m.scheduleEndDate || m.endDate
            } as IFreeSlotItem;
          }
        });
      return courtSchedule;
    });
    return day;
  });
  return filteredSchedule;
}

export function getMatchDurationFromConstraints(
  match: IMatchScheduleItem,
  constraints: IScheduleConstraints | null,
  classObject?: Class | null
): number {
  if (!constraints) return 25;

  let roundKey = 'round_1'; // default fallback

  const isBoxLeague =
    classObject?.format === ClassFormatEnum.BOX_LEAGUE ||
    classObject?.format === ClassFormatEnum.BOX_LEAGUE_ELIM;

  // Map match type to roundKey
  if (match.tournamentGroup) {
    const boxRound = Number(match.type);
    if (
      isBoxLeague &&
      Number.isFinite(boxRound) &&
      boxRound >= 1 &&
      boxRound <= 10
    ) {
      roundKey = `round_${Math.floor(boxRound)}`;
    } else {
      roundKey = 'group';
    }
  } else if (match.type === ClassMatchType.FINAL) {
    roundKey = 'round_13';
  } else if (match.type === ClassMatchType.SEMIFINAL) {
    roundKey = 'round_12';
  } else if (match.type === ClassMatchType.QUARTERFINAL) {
    roundKey = 'round_11';
  } else if (typeof match.type === 'number' && match.type <= 10) {
    // Standard knockout rounds 1-10
    roundKey = `round_${match.type}`;
  }

  const classOverrides = constraints.classOverrides?.[Number(match.classId)];
  if (classOverrides?.matchLengths && classOverrides.matchLengths[roundKey]) {
    return classOverrides.matchLengths[roundKey];
  }

  const globalLengths = (constraints as IScheduleConstraints).globalMatchLengths;
  if (globalLengths && globalLengths[roundKey]) {
    return globalLengths[roundKey];
  }

  return 30;
}

export function getTournamentTimezone(
  country?: string | null,
  tz?: string | null,
  scheduleTimezone?: string | null,
): string {
  if (tz) return tz;
  if (scheduleTimezone) return scheduleTimezone;
  if (country) return getTimezoneForCountry(country) || 'UTC';
  return 'UTC';
}

export function getColorForClass(classId: number) {
  return webColors[classId % webColors.length];
}

const webColors = [
  'goldenrod',
  'mediumorchid',
  'mediumvioletred',
  'brown',
  'navy',
  'darkolivegreen',
  'springgreen',
  'darkturquoise',
  'palevioletred',
  'saddlebrown',
  'deepskyblue',
  'coral',
  'burlywood',
  'maroon',
  'darkmagenta',
  'darkblue',
  'slateblue',
  'cornflowerblue',
  'green',
  'darksalmon',
  'blueviolet',
  'magenta',
  'indianred',
  'darkkhaki',
  'lightskyblue',
  'mediumturquoise',
  'chocolate',
  'teal',
  'darkred',
  'crimson',
  'lightsteelblue',
  'orangered',
  'sienna',
  'olivedrab',
  'lightsalmon',
  'palegreen',
  'darkslateblue',
  'red',
  'cadetblue',
  'rebeccapurple',
  'rosybrown',
  'limegreen',
  'orchid',
  'khaki',
  'purple',
  'darkviolet',
  'mediumseagreen',
  'dodgerblue',
  'salmon',
  'skyblue',
  'lawngreen',
  'blue',
  'aquamarine',
  'tomato',
  'royalblue',
  'mediumaquamarine',
  'firebrick',
  'darkorange',
  'chartreuse',
  'turquoise',
  'darkorchid',
  'lightseagreen',
  'midnightblue',
  'mediumblue',
  'indigo',
  'tan',
  'darkgoldenrod',
  'darkcyan',
  'hotpink',
  'steelblue',
  'seagreen',
  'lime',
  'violet',
  'yellowgreen',
  'fuchsia',
  'peru',
  'deeppink',
  'black',
  'lightcoral',
  'olive',
  'sandybrown',
  'mediumpurple',
];
